/**
 * HIP-4 outcome ids. This module is the ONLY place that computes outcome encodings, coins and asset ids.
 * Facts (docs/research/OUTCOME_IDS.md, #658):
 *   encoding  = 10 * outcome + side           side 0 = sideSpecs[0] (Yes / A / Over), 1 = sideSpecs[1]
 *   coin      = "#<encoding>"                 orders, books, trades, fills, WS
 *   balance   = "+<encoding>"                 spotState / spotClearinghouseState rows
 *   asset id  = 100_000_000 + encoding        order / cancel `a` field
 *   sizes are whole shares; prices have at most 5 decimals and lie strictly in (0, 1).
 */

import { parseDescription } from '../describe/parse';

export const OUTCOME_ASSET_OFFSET = 100_000_000;
export const OUTCOME_PX_DECIMALS = 5;

/** 0 = sideSpecs[0] (Yes / participant A / Over), 1 = sideSpecs[1] (No / B / Under). */
export type Side = 0 | 1;

export interface SideSpec {
  name: string;
}

/** Element of `outcomeMeta.outcomes`. */
export interface OutcomeMeta {
  outcome: number;
  name: string;
  description: string;
  sideSpecs: [SideSpec, SideSpec] | SideSpec[];
  quoteToken: string;
  venue?: string;
  deployerFeeScale?: string;
}

export interface OutcomeRef {
  outcome: number;
  side: Side;
  encoding: number;
}

function checkOutcome(outcome: number): void {
  if (!Number.isSafeInteger(outcome) || outcome < 0) throw new Error(`invalid outcome id: ${outcome}`);
}

function checkSide(side: number): asserts side is Side {
  if (side !== 0 && side !== 1) throw new Error(`invalid outcome side: ${side}`);
}

/** 10*outcome + side. Throws on a non-integer or negative outcome, or a side other than 0|1. */
export function encoding(outcome: number, side: Side): number {
  checkOutcome(outcome);
  checkSide(side);
  return 10 * outcome + side;
}

/** WS / l2Book / trades / order & fill coin: "#<encoding>". */
export function coin(outcome: number, side: Side): string {
  return `#${encoding(outcome, side)}`;
}

/** Spot balance coin as it appears in spotState / spotClearinghouseState: "+<encoding>". */
export function balanceCoin(outcome: number, side: Side): string {
  return `+${encoding(outcome, side)}`;
}

/** Alias of {@link balanceCoin} (the docs call it the token name). */
export const tokenName = balanceCoin;

/** Order/cancel `a` field: 100_000_000 + encoding. */
export function assetId(outcome: number, side: Side): number {
  return OUTCOME_ASSET_OFFSET + encoding(outcome, side);
}

function fromEncoding(enc: number): OutcomeRef | null {
  if (!Number.isSafeInteger(enc) || enc < 0) return null;
  const side = enc % 10;
  if (side !== 0 && side !== 1) return null;
  return { outcome: (enc - side) / 10, side, encoding: enc };
}

/** Parse "#E", "+E" or an asset id (number >= 1e8) into {outcome, side, encoding}; null if not an outcome. */
export function fromCoin(c: string | number): OutcomeRef | null {
  if (typeof c === 'number') {
    if (!Number.isSafeInteger(c) || c < OUTCOME_ASSET_OFFSET) return null;
    return fromEncoding(c - OUTCOME_ASSET_OFFSET);
  }
  const m = /^[#+](\d+)$/.exec(c);
  if (!m || m[1] === undefined) return null;
  return fromEncoding(Number(m[1]));
}

/**
 * Coin of a fill (#788 settlement history) into {outcome, side, encoding}; null if not an outcome. Accepts what
 * {@link fromCoin} accepts as a string ("#E", "+E") plus the older testnet fill form "@<asset id>" ("@100000141" =
 * outcome 14 side 1): early testnet outcomes (USDH era) reported fills under the spot-style "@" + asset id. A plain
 * spot pair ("@107", index < 1e8) is not an outcome.
 */
export function fromSettlementCoin(c: string): OutcomeRef | null {
  if (typeof c !== 'string') return null;
  const m = /^@(\d+)$/.exec(c);
  if (m && m[1] !== undefined) {
    const n = Number(m[1]);
    return n >= OUTCOME_ASSET_OFFSET ? fromCoin(n) : null;
  }
  return fromCoin(c);
}

/** The same market seen from the other side (#E0 <-> #E1); px' = 1 - px. */
export function dual(side: Side): Side {
  checkSide(side);
  return side === 0 ? 1 : 0;
}

/** `key:value|key:value` description into a map (values may contain ':'); see describe/parse.ts. */
export { parseDescription };

/** Human side label: "Yes" → "Yes", "template:Yes" → "Yes", "template:{shortNameA}" → description's shortNameA. */
export function sideName(meta: Pick<OutcomeMeta, 'description' | 'sideSpecs'>, side: Side): string {
  checkSide(side);
  const fallback = side === 0 ? 'Yes' : 'No';
  const raw = meta.sideSpecs[side]?.name;
  if (!raw) return fallback;
  if (!raw.startsWith('template:')) return raw;
  const tpl = raw.slice('template:'.length);
  if (!tpl.includes('{')) return tpl || fallback;
  const desc = parseDescription(meta.description);
  let missing = false;
  const res = tpl.replace(/\{(\w+)\}/g, (_, key: string) => {
    const v = desc[key];
    if (v === undefined || v === '') {
      missing = true;
      return '';
    }
    return v;
  });
  return missing || res.trim() === '' ? fallback : res;
}

function stripZeros(s: string): string {
  if (!s.includes('.')) return s;
  return s.replace(/0+$/, '').replace(/\.$/, '');
}

/** Price to wire string: rounded to 5 decimals, must lie strictly in (0, 1), trailing zeros stripped. */
export function toWirePx(px: number): string {
  if (!Number.isFinite(px)) throw new Error(`invalid price: ${px}`);
  const s = stripZeros(px.toFixed(OUTCOME_PX_DECIMALS));
  const v = Number(s);
  if (!(v > 0 && v < 1)) throw new Error(`outcome price must be in (0, 1) after rounding to 5 decimals: ${px}`);
  return s;
}

/** Size to wire string: whole shares (rounded), must be >= 1. */
export function toWireSz(sz: number): string {
  if (!Number.isFinite(sz)) throw new Error(`invalid size: ${sz}`);
  const n = Math.round(sz);
  if (n < 1) throw new Error(`outcome size must be at least 1 whole share: ${sz}`);
  return String(n);
}
